import React, { useState, useEffect, useContext } from "react";
import { Col, Row, Button } from '@themesberg/react-bootstrap';
import { GeneralInfoForm } from "../../components/Forms";
import { ChoosePhotoWidget } from "../../components/Widgets";
import { ToastAlert } from "../../components/ToastAlert";
import API from "../../utils/api";
import { UserContext } from "../../utils/UserContext";
import { useTranslation } from "react-i18next";
export default function Profile() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [profileExists, setProfileExists] = useState(false);
  const [userType, setUserType] = useState("private"); // default "private"
  const backendURL = `https://api-dashboard.nordmann-inkasso.de`;
  const { profile, setProfile } = useContext(UserContext);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchProfile() {
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const res = await API.get("/profile/me", config);
        if (res.data) {
          setProfileExists(true);
          setProfile(res.data);
          setUserType(res.data.userType || "private");

          setFormData({
            fullName: res.data.fullName || "",
            companyName: res.data.companyName || "",
            street: res.data.address?.split(",")[0]?.trim() || "",        // 👈 split address
            postalCity: res.data.address?.split(",").slice(1).join(",").trim() || "",
            email: res.data.email || "",
            phone: res.data.phone || "",
            eligibility: res.data.eligibility || false,
            currentPassword: "",
            newPassword: "",
          });

          if (res.data.profilePicture) {
            setPreviewImage(`${backendURL}/${res.data.profilePicture.replace(/\\/g, "/")}`);
          }
        } else {
          setProfileExists(false);
          setProfile(null);
        }
      } catch (error) {
        console.error("Failed to load profile", error);
        setProfileExists(false);
        setProfile(null);
      }
    }
    fetchProfile();
  }, []);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant });
  };

  const handleFileSelect = (file) => {
    setProfileImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setLoading(true); // start loader
    // Required fields based on user type
    const requiredFields = userType === "company"
      ? ["fullName", "companyName", "street", "postalCity", "email"]
      : ["fullName", "street", "postalCity", "email"];

    // Check required fields
    for (let field of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === "") {
        showAlert(`Please fill in the ${field} field`, "danger");
        setLoading(false); // stop loader
        return; // stop submission
      }
    }

    // Email validation
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      showAlert("Please enter a valid email address", "danger");
      setLoading(false); // stop loader
      return;
    }

    // Address format validation
    // Expected format: "Street + Number, Zipcode, City"
    const addressRegex = /^(?=.*[A-Za-zÄÖÜäöüß])(?=.*\d)(?=.*\b\d{4,6}\b)(?=.*[A-Za-zÄÖÜäöüß]).+$/;
    if (!formData.street || !formData.postalCity) {
      showAlert("Please fill in both Street and Postal/City fields", "danger");
      setLoading(false); // stop loader
      return;
    }

    if (!formData.phone) {
      showAlert("Please fill the Phone field", "danger");
      setLoading(false); // stop loader
      return;
    }
    // Phone validation (optional)
    if (formData.phone && !/^(\+?\d{1,3} ?\d{7,12}|\d{7,15})$/.test(formData.phone)) {
      showAlert(
        "Telephone is in wrong format. Suggested: 0123456789 or +49 123456789",
        "danger"
      );
      setLoading(false); // stop loader
      return;
    }
    const fullAddress = `${formData.street}, ${formData.postalCity}`.trim();

    // Submit form
    const data = new FormData();
    data.append("userType", userType);
    data.append("address", fullAddress);
    for (const key in formData) {
      if (key !== "street" && key !== "postalCity") {
        data.append(key, formData[key]);
      }
    }
    if (profileImage) data.append("profilePicture", profileImage);

    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      };

      const response = profileExists
        ? await API.patch("/profile/me", data, config)
        : await API.post("/profile", data, config);

      if (![200, 201].includes(response.status)) throw new Error("Failed to save profile");

      // showAlert("Profile saved successfully!", "success");
      setProfileExists(true);
      setProfile(response.data);
      // If user filled currentPassword & newPassword, call password API
      if (formData.currentPassword || formData.newPassword) {
        try {
          const token = localStorage.getItem("token");
          const config = { headers: { Authorization: `Bearer ${token}` } };

          const passResponse = await API.post("/auth/change-password", {
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword
          }, config);

          if (passResponse.status !== 200) {
            showAlert(passResponse.data.message || "Error changing password", "danger");
            return;
          }
          else{
            showAlert("Profile updated successfully!", "success");
          }

          // showAlert("Profile and password updated successfully!", "success");
          // Clear password fields
          setFormData(prev => ({ ...prev, currentPassword: "", newPassword: "" }));
        } catch (error) {
          console.error(error);
          const msg =
            error.response?.data?.message || "Error changing password";

          showAlert(msg, "danger");
        }
      } else {
        showAlert("Profile updated successfully!", "success");
      }

    } catch (error) {
      showAlert("Error: " + error.message, "danger");
    }
    finally {
      setLoading(false); // stop loader
    }
  };



  return (
    <>
      <ToastAlert
        show={alert.show}
        onClose={() => setAlert({ ...alert, show: false })}
        message={alert.message}
        variant={alert.variant}
      />
      <Row className="flex-column-reverse flex-xl-row">
        <Col xs={12} xl={8}>
          <GeneralInfoForm
            formData={formData}
            onChange={handleFormChange}
            userType={userType}
            setUserType={setUserType}
            profileExists={profileExists}
          />
          {/* <Button className="mt-3" >{t("profilePageTranslations.saveButton")}</Button> */}
          <Button onClick={handleSubmit} className="mt-3" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              t("profilePageTranslations.saveButton")
            )}
          </Button>

        </Col>

        <Col xs={12} xl={4}>
          <ChoosePhotoWidget
            title={t("profilePageTranslations.selectPhotoTitle")}
            photo={previewImage}
            onFileSelect={handleFileSelect}
          />
        </Col>
      </Row>
    </>
  );
}
